<template>
  <BlBackdrop :footer="false" backdropClass="overflow-hidden">
    <div class="flex" :style="{ height: '80vh', width: '80vw' }">
      <div class="w-160 flex-shrink-0">
        <div
          class="h-12 w-160 px-6 bg-slate-300 border-b border-slate-400 flex items-center gap-2"
        >
          <BlTextBody2
            :text="
              displayDate({ date: overlapped.date, format: 'dddd, DD MMM' })
            "
          />
          <div class="h-1 w-1 flex-shrink-0 bg-slate-600 rounded"></div>
          <BlTextBody2 :text="overlapped.room.name" />
        </div>
        <div
          class="flex flex-col relative bg-slate-200 overflow-y-auto scrollbar-lg"
          :style="{ height: 'calc(80vh - 3rem)' }"
        >
          <div
            v-for="time in clockRound"
            :key="time"
            class="flex"
            :id="`time_view_${time}`"
          >
            <div
              class="w-20 py-2 px-3 flex-shrink-0 bg-slate-200 flex justify-end border-b border-r border-slate-400 transition-all duration-300"
              :style="{ height: `${slotHeight * 2}rem` }"
            >
              <BlTextBody2 :text="time" />
            </div>

            <div
              class="w-full flex flex-col gap-2 items-center justify-center border-b border-r border-slate-400 bg-slate-200 transition-all duration-300"
              :style="{
                height: `${slotHeight * 2}rem`
              }"
            ></div>
          </div>

          <AvailableTimeslot
            v-for="(item, idx) in availableTimeslots"
            :key="idx"
            :id="`overlapped-${idx}`"
            :isCompact="true"
            :item="item"
            :order="idx"
            :slotHeight="slotHeight"
            :slotWidth="slotWidth"
            :selected="selectedAvailabilityId"
            @set-selected="onSelectAvailabilityId($event)"
          />
        </div>
      </div>
      <div class="h-full w-full flex flex-col items-center">
        <div
          class="h-full w-full max-w-152 px-10 flex flex-col justify-center gap-2"
        >
          <BlTextTitle4 text="Adjust conflicting timeslots" />
          <div>
            <div>
              <BlTextBody2
                text="There are 2 overlapping availability slots in"
              />
              <BlTextBody2 :text="overlapped.room.name" />
              <BlTextBody2 text="on" />
              <BlTextBody2
                :text="
                  `${displayDate({
                    date: overlapped.date,
                    format: 'dddd, DD MMMM'
                  })}:`
                "
              />
            </div>
            <ul class="list-inside list-disc">
              <li v-for="(item, idx) in availableTimeslots" :key="idx">
                <BlTextBody2 :text="item.start_time_string" class="font-bold" />
                <BlTextBody2 text="-" class="font-bold" />
                <BlTextBody2 :text="item.end_time_string" class="font-bold" />
                <BlTextBody2 text="by" />
                <BlTextBody2 :text="item.therapist.name" />
              </li>
            </ul>
          </div>
          <div v-if="bookedTimeslots.length === 2" class="flex flex-col gap-2">
            <div>
              <BlTextBody2 text="Resolve the" />
              <BlTextBody2 :text="bookedTimeslots[0].periods.length" />
              <BlTextBody2 text="appointment(s) for" />
              <BlTextBody2 :text="bookedTimeslots[0].therapist.name" />
              <BlTextBody2 text="or" class="font-bold" />
              <BlTextBody2 text="the" />
              <BlTextBody2 :text="bookedTimeslots[1].periods.length" />
              <BlTextBody2 text="appointment(s) for" />
              <BlTextBody2 :text="bookedTimeslots[1].therapist.name" />
              <BlTextBody2
                text="before you can delete the conflicting availability slot."
              />
            </div>
            <div v-for="(timeslot, idx) in bookedTimeslots" :key="idx">
              <div class="px-5 py-2 bg-slate-200 rounded truncate">
                <BlTextSubtitle3
                  :text="timeslot.therapist.name"
                  class="truncate"
                />
              </div>
              <ResolveAppointment
                v-for="period in timeslot.periods"
                :key="period.id"
                :period="period"
                @after-commit="$emit('after-commit')"
              />
            </div>
          </div>
          <div v-if="bookedTimeslots.length === 1" class="flex flex-col gap-2">
            <div>
              <BlTextBody2
                text="To resolve the conflict, we'll remove the availability slot with no appointments, which belongs to"
              />
              <BlTextBody2
                :text="selectedAvailability.therapist.name"
                class="font-bold"
              />
              <BlTextBody2
                :text="
                  `(${selectedAvailability.overlapped_start_time} - ${selectedAvailability.overlapped_end_time}).`
                "
                class="font-bold"
              />
            </div>
          </div>
          <div v-if="!bookedTimeslots.length" class="flex flex-col gap-2">
            <BlTextBody2
              text="To resolve the conflict, select the slot to delete from the calendar on the left."
            />
            <div
              v-if="!selectedAvailabilityId"
              class="px-2 py-2 flex flex-col gap-2 bg-slate-200 border border-slate-300 rounded"
            >
              <BlTextBody2 text="No timeslot selected." />
            </div>
            <div
              v-else
              class="px-2 py-2 flex flex-col gap-2 bg-slate-200 border border-slate-300 rounded"
            >
              <BlTextBody2 text="Selected slot to be deleted:" />
              <div class="flex items-center gap-1">
                <BlIcon icon="user" color="slate-6" class="h-4 w-4" />
                <BlTextBody2 :text="selectedAvailability.therapist.name" />
              </div>
              <div class="flex items-center gap-1">
                <BlIcon icon="clock" color="slate-6" class="h-4 w-4" />
                <BlTextTitle4
                  :text="selectedAvailability.overlapped_start_time"
                />
                <BlTextTitle4 text="-" />
                <BlTextTitle4
                  :text="selectedAvailability.overlapped_end_time"
                />
              </div>
            </div>
          </div>

          <div
            v-if="
              selectedAvailability &&
                selectedAvailability.availability &&
                selectedAvailability.availability.repetition_type !==
                  'not_repeat'
            "
            class="flex items-start gap-2 p-2 bg-cornflower-200 rounded"
          >
            <BlIcon icon="info" color="cornflower-8" class="w-4 mt-1" />
            <div class="break-words">
              <BlTextBody2
                text="You've adjusted a recurring slot, but this change applies only to this one."
                class="text-cornflower-800 break-words"
              />
              <BlTextBody2
                text="To update all remaining slots, please go to the"
                class="text-cornflower-800 break-words"
              />
              <router-link
                :to="`/availabilities?date=${overlapped.date}&type=daily`"
              >
                <BlTextBody2
                  text="Availabilities"
                  class="text-cornflower-800 break-words font-bold underline"
                />
              </router-link>
              <BlTextBody2
                text="page."
                class="text-cornflower-800 break-words"
              />
            </div>
          </div>
        </div>
        <div
          class="h-24 w-full max-w-152 px-10 pt-4 grid grid-cols-4 items-start flex-shrink-0 gap-4"
        >
          <BlInputButton
            label="Cancel"
            type="plain"
            @clicked="$emit('close-modal')"
          />
          <BlInputButton
            label="Delete selected slot"
            type="error"
            class="col-span-3"
            :disabled="!selectedAvailabilityId"
            :loading="submitLoading"
            @clicked="onSubmit"
          />
        </div>
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import { displayDate } from "@/plugins/helper.js";
import { CLOCK_ROUND, CLOCK_HALF } from "@/plugins/assets.js";
import AvailableTimeslot from "./AvailableTimeslot.vue";
import ResolveAppointment from "./ResolveAppointment.vue";

import BlBackdrop from "../../BlBackdrop.vue";
import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "ResolveOverlappedModal",
  components: {
    AvailableTimeslot,
    ResolveAppointment,
    BlBackdrop,
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlInputButton
  },
  props: {
    overlapped: { type: Object, default: null }
    // @close-modal
    // @after-commit
  },
  data() {
    return {
      submitLoading: false,
      bookedTimeslots: [],
      selectedAvailabilityId: 0,
      clockRound: CLOCK_ROUND,
      clockHalf: CLOCK_HALF
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    slotHeight() {
      // retun value in rem, 1 slot is 30 minutes
      return 8.75 / 2;
    },
    slotWidth() {
      return "12rem";
    },
    selectedAvailability() {
      if (!this.selectedAvailabilityId) return null;
      const timeslot = this.availableTimeslots.find(
        i => i.availability_id === this.selectedAvailabilityId
      );
      return {
        availability_id: this.selectedAvailabilityId,
        therapist: timeslot.therapist,
        overlapped_start_time: this.overlapped.start_time,
        overlapped_end_time: this.overlapped.end_time,
        ...timeslot
      };
    },
    availableTimeslots() {
      const arr = [];
      const date = this.overlapped.date ? this.overlapped.date : null;
      const room = this.overlapped.room ? this.overlapped.room : null;
      const key = date ? { id: date, name: date } : room;

      this.overlapped.timeslots.forEach(slot => {
        const unique = `${key.id}_${slot.availability_id}`;
        const therapist = {
          id: slot.availability.user_id,
          name: slot.availability.user_name
        };
        const room = slot.availability.room;
        const usedDate = slot.date;

        const lastIndex = arr.findIndex(i => i.unique === unique);

        const indexStart = this.clockHalf.findIndex(
          i => i === this.overlapped.start_time
        );
        const indexEnd = this.clockHalf.findIndex(
          i => i === this.overlapped.end_time
        );
        const indexStartSlot = this.clockHalf.findIndex(
          i => i === slot.start_time_string
        );
        const indexEndSlot = this.clockHalf.findIndex(
          i => i === slot.end_time_string
        );

        const disabled =
          indexEndSlot <= indexStart || indexStartSlot >= indexEnd;
        const readOnly = this.bookedTimeslots.length > 0;

        if (lastIndex > -1) {
          arr[lastIndex].end_time_string = slot.end_time_string;
          arr[lastIndex].slots.push({ ...slot, unique, disabled, readOnly });
        } else {
          const res = {
            key,
            unique,
            therapist,
            user: therapist,
            room,
            date: usedDate,
            //
            availability_id: slot.availability_id,
            availability: slot.availability,
            start_time_string: slot.start_time_string,
            end_time_string: slot.end_time_string,
            primary_color: slot.primary_color,
            secondary_color: slot.secondary_color,
            slots: [{ ...slot, unique, disabled, readOnly }]
          };
          arr.push(res);
        }
      });
      return arr;
    }
  },
  watch: {},
  async mounted() {
    setTimeout(() => {
      const el = document.getElementById("overlapped-0");
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
      }
    }, 300);

    let empty = true;
    const arr = [];
    this.availableTimeslots.forEach(timeslot => {
      const periods = [];
      const therapist = timeslot.therapist;
      timeslot.slots.forEach(slot => {
        let bookedInInterception = false;
        if (slot.is_booked) {
          empty = false;

          const indexStart = this.clockHalf.findIndex(
            i => i === this.overlapped.start_time
          );
          const indexEnd = this.clockHalf.findIndex(
            i => i === this.overlapped.end_time
          );
          const indexStartSlot = this.clockHalf.findIndex(
            i => i === slot.start_time_string
          );
          const indexEndSlot = this.clockHalf.findIndex(
            i => i === slot.end_time_string
          );

          if (indexStartSlot >= indexStart && indexEndSlot <= indexEnd) {
            bookedInInterception = true;
          }
          if (indexStartSlot > indexEnd) bookedInInterception = false;
          if (indexEndSlot < indexStart) bookedInInterception = false;

          const lastIndex = periods.findIndex(
            i => i.appointment_id === slot.appointment_id
          );
          if (lastIndex > -1 && slot.appointment_id) {
            periods[lastIndex].end_time_string = slot.end_time_string;
            periods[lastIndex].count += 1;
            periods[lastIndex].booked_in_interception = bookedInInterception;
          } else {
            periods.push({
              ...slot,
              count: 1,
              booked_in_interception: bookedInInterception
            });
          }
        }
      });
      const indexofBookedInInterception = periods.findIndex(
        i => i.booked_in_interception
      );
      arr.push({
        timeslot,
        therapist,
        periods,
        booked_in_interception: indexofBookedInInterception > -1
      });
    });
    const bookeds = arr.filter(
      i => i.periods.length && i.booked_in_interception
    );
    if (!empty) {
      this.bookedTimeslots = bookeds;
      if (bookeds.length === 1) {
        const bookedId = bookeds[0].timeslot.availability_id;
        this.availableTimeslots.forEach(i => {
          if (i.availability_id !== bookedId) {
            this.selectedAvailabilityId = i.availability_id;
          }
        });
      }
    }
  },
  methods: {
    ...mapActions(["showToast", "actionPatch", "actionDelete"]),
    displayDate,
    onSelectAvailabilityId(id) {
      if (!this.bookedTimeslots.length) {
        this.selectedAvailabilityId = id;
      }
    },
    onSubmit() {
      if (
        this.selectedAvailability.start_time_string ===
          this.selectedAvailability.overlapped_start_time &&
        this.selectedAvailability.end_time_string ===
          this.selectedAvailability.overlapped_end_time
      ) {
        this.onDelete();
      } else {
        this.onUpdate();
      }
    },
    onDelete() {
      const data = {
        url: `/api/v1/availabilities/${this.selectedAvailability.availability_id}?date=${this.selectedAvailability.date}&delete_option=current`
      };
      this.submitLoading = true;
      this.actionDelete(data).then(({ success }) => {
        if (!success) {
          this.submitLoading = false;
          return;
        }
        const title = "Availability deleted";
        const message = `Success! Availability slot for <b>${this.selectedAvailability.therapist.name}</b> has been deleted.`;
        this.showToast({ title, message, type: "success" });
        this.$emit("after-commit");
        setTimeout(() => {
          this.submitLoading = false;
          this.$emit("close-modal");
        }, 1500);
      });
    },
    onUpdate() {
      const overlappedStart = this.selectedAvailability.overlapped_start_time;
      const overlappedEnd = this.selectedAvailability.overlapped_end_time;

      const indexStart = this.clockHalf.findIndex(i => i === overlappedStart);
      const indexEnd = this.clockHalf.findIndex(i => i === overlappedEnd);
      const slotsTop = { slots: [], has_booked: false };
      const slotsCurrent = { slots: [], has_booked: false };
      const slotsBottom = { slots: [], has_booked: false };
      this.selectedAvailability.slots.forEach(slot => {
        const start = this.clockHalf.findIndex(
          i => i === slot.start_time_string
        );
        const end = this.clockHalf.findIndex(i => i === slot.end_time_string);
        if (end <= indexStart) {
          slotsTop.slots.push(slot);
          if (slot.is_booked) slotsTop.has_booked = true;
        }
        if (start >= indexStart && end <= indexEnd) {
          slotsCurrent.slots.push(slot);
          if (slot.is_booked) slotsCurrent.has_booked = true;
        }
        if (start >= indexEnd) {
          slotsBottom.slots.push(slot);
          if (slot.is_booked) slotsBottom.has_booked = true;
        }
      });

      let slotsUsed = { slots: [], has_booked: false };
      if (slotsTop.has_booked) {
        slotsUsed = slotsTop.slots;
      } else if (slotsBottom.has_booked) {
        slotsUsed = slotsBottom.slots;
      } else {
        slotsUsed =
          slotsTop.slots.length > slotsBottom.slots.length
            ? slotsTop.slots
            : slotsBottom.slots;
      }

      if (slotsCurrent.has_booked) {
        const title = "Failed to update availability";
        const message = `Failed! Please cancel or reschedule appointment(s) slot for <b>${this.selectedAvailability.therapist.name}</b> before update availability.`;
        this.showToast({ title, message, type: "error" });
        return;
      }
      if (slotsTop.has_booked && slotsBottom.has_booked) {
        const title = "Failed to update availability";
        const message = `Failed! Please cancel or reschedule appointment(s) slot for <b>${this.selectedAvailability.therapist.name}</b> on top-side or bottom-side before update availability.`;
        this.showToast({ title, message, type: "error" });
        return;
      }
      const startTime = slotsUsed[0].start_time_string;
      const endTime = slotsUsed[slotsUsed.length - 1].end_time_string;
      const data = {
        url: `/api/v1/availabilities/${this.selectedAvailabilityId}`,
        params: {
          availability: {
            start_time: startTime,
            end_time: endTime,
            //
            date: this.overlapped.date,
            update_option: "current"
          }
        }
      };
      this.submitLoading = true;
      this.actionPatch(data).then(({ success }) => {
        if (!success) {
          this.submitLoading = false;
          return;
        }
        const title = "Availability updated";
        const message = `Success! Availability slot for <b>${this.selectedAvailability.therapist.name}</b> has been updated.`;
        this.showToast({ title, message, type: "success" });
        this.$emit("after-commit");
        setTimeout(() => {
          this.submitLoading = false;
          this.$emit("close-modal");
        }, 1500);
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/overlapped/ResolveOverlappedModal.vue